import {
  useState,
  useCallback,
  useRef,
  useEffect,
} from 'react';
import { useTranslation } from 'react-i18next';
import {
  ExTable,
  ExPagination,
  ExEmptyState,
  ExTooltip,
  TooltipPosition,
  TooltipAlignment,
  ExLoader,
  LoaderSize,
  LoaderVariant,
} from '@boomi/exosphere';
import { createRoot } from 'react-dom/client';
import noResultsImage from '@assets/boomi-ai/ui-kit/agentFoundation/No_result.svg';
import { ModelsTableProps } from '../types/models.types';
import ModelRowActions from '../ModelRowActions/ModelRowActions';
import {
  providerLogo,
} from '@components/ModelsManagement';
import './ModelsTable.scss';
import { formatDate } from '@components/ListViewUtils/ListViewUtils';

const ITEMS_PER_PAGE_OPTIONS = [20, 50];

function ModelsTable({
  models = [],
  isLoading = false,
  onModelSelect,
  onEdit,
  onDelete,
  onViewModels,
  showEmptyState = true,
  searchTerm = '',
  isAnyFilterActive = false,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  setShowEmptyState,
  // setModels,
  // setIsLoading,
  hasEditPermission = false,
  hasCreatePermission = false,
  currentPage = 1,
  pageSize = 20,
  totalItems = 0,
  onPageChange,
  onPageSizeChange,
  onSortChange,
  isInitialLoading = isLoading,
}: Readonly<ModelsTableProps & {
  currentPage?: number;
  pageSize?: number;
  totalItems?: number;
  onPageChange?: (page: number) => void;
  onPageSizeChange?: (pageSize: number) => void;
  onSortChange?: (sortBy?: string, sortOrder?: string) => void;
  isAnyFilterActive?: boolean;
  isInitialLoading?: boolean;
}>) {
  const [t] = useTranslation('common');
  const [gridApi, setGridApi] = useState<any>(null);
  const gridApiRef = useRef<any>(null);
  const sortModelRef = useRef<any[]>([]);
  const [sortModel, setSortModel] = useState<any[]>([]);
  const openDropdownRef = useRef<any>(null);

  const closeOpenDropdown = () => {
    if (openDropdownRef.current && typeof openDropdownRef.current.closeDropdown === 'function') {
      openDropdownRef.current.closeDropdown();
      openDropdownRef.current = null;
    }
  };

  const actionCellRenderer = useCallback((params: any) => {
    const { data } = params;
    const container = document.createElement('div');
    const root = createRoot(container);

    root.render(
      <div style={{ display: 'flex', paddingTop: 'var(--exo-spacing-x-small)' }}>
        <ModelRowActions
          model={data}
          onView={() => { closeOpenDropdown(); onModelSelect?.(data); }}
          onEdit={onEdit}
          onDelete={onDelete}
          t={t}
          openDropdownRef={openDropdownRef}
        />
      </div>,
    );

    return container;
  }, [onModelSelect, onEdit, onDelete, t]);

  const nameCellRenderer = useCallback((params: any) => {
    const container = document.createElement('div');
    container.className = 'models-table__name-cell';
    const root = createRoot(container);
    const logo = providerLogo(params.data.provider);

    root.render(
      <div style={{
        display: 'flex',
        alignItems: 'center',
        width: '100%',
        minWidth: 0,
        paddingTop: '4px',
      }}
      >
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flex: '0 0 32px',
          width: '32px',
          minWidth: '32px',
          maxWidth: '32px',
          height: '32px',
          minHeight: '32px',
          maxHeight: '32px',
          boxSizing: 'border-box',
          backgroundColor: 'var(--application-bg-color)',
          borderRadius: '4px',
        }}
        >
          <img
            src={logo.src}
            alt={logo.alt}
            style={{
              display: 'block',
              flex: '0 0 20px',
              width: '20px',
              minWidth: '20px',
              maxWidth: '20px',
              height: '20px',
              minHeight: '20px',
              maxHeight: '20px',
              boxSizing: 'border-box',
              objectFit: 'contain',
            }}
          />
        </div>
        <div style={{ marginLeft: '8px', overflow: 'hidden' }}>
          <ExTooltip
            tooltipDelay={0}
            position={TooltipPosition.TOP}
            alignment={TooltipAlignment.MIDDLE}
          >
            <div style={{
              overflowWrap: 'anywhere',
              width: '100%',
              overflow: 'hidden',
              whiteSpace: 'normal',
            }}
            >
              {params.value}
            </div>
            <button
              type="button"
              slot="anchor"
              className="models-table__model-name-link"
              onClick={() => onModelSelect?.(params.data)}
              style={{
                display: 'block',
                background: 'none',
                border: 'none',
                fontSize: 'var(--exo-font-size-small)',
                paddingLeft: 0,
                color: 'var(--exo-color-font-link)',
                cursor: 'pointer',
                fontWeight: 400,
                textDecoration: 'underline',
                lineHeight: '15px',
                paddingBottom: '6px',
                paddingTop: '0',
                maxWidth: '100%',
                fontFamily: 'inherit',
              }}
            >
              {params.value}
            </button>
          </ExTooltip>
          <div style={{
            color: 'var(--exo-color-font-secondary)',
            fontWeight: 400,
            fontSize: '12px',
            lineHeight: '15px',
          }}
          >
            {t('id')}
            :
            {' '}
            {params.data.id}
          </div>
        </div>
      </div>,
    );

    return container;
  }, [onModelSelect, t]);

  const modelCountCellRenderer = useCallback((params: any) => {
    if (params.value === 0) {
      return params.value;
    }
    const container = document.createElement('div');
    const root = createRoot(container);
    const agentCountStyle: React.CSSProperties = {
      background: 'none',
      border: 'none',
      textDecoration: 'underline',
      padding: 'var(--exo-spacing-none)',
      color: 'var(--exo-color-font-link)',
      fontFamily: 'var(--exo-font-family)',
      fontWeight: 400,
      fontSize: '14px',
      lineHeight: '150%',
      letterSpacing: '-2%',
      cursor: 'pointer',
    };
    root.render(
      <button
        type="button"
        className="models-table__agents-link"
        onClick={() => {
          onViewModels?.(params.data.id, params.data.name, params.data.provider);
        }}
        style={agentCountStyle}
      >
        {params.value}
      </button>,
    );

    return container;
  }, [onViewModels]);

  const environmentCellRenderer = useCallback((params: any) => {
    const container = document.createElement('div');
    const root = createRoot(container);

    const environments = params.data.environments || [];
    const envNames = environments.map((env: any) => env.environment_name);
    const firstTwo = envNames.slice(0, 1);
    const remaining = envNames.length - 1;
    const displayText = remaining > 0
      ? `${firstTwo.join(', ')} (+${remaining})`
      : firstTwo.join(', ');

    root.render(
      <div style={{ marginLeft: '8px', overflow: 'hidden' }}>
        <ExTooltip
          tooltipDelay={0}
          position={TooltipPosition.TOP}
          alignment={TooltipAlignment.MIDDLE}
        >
          <div style={{
            overflowWrap: 'anywhere',
            width: '100%',
            overflow: 'hidden',
            whiteSpace: 'normal',
          }}
          >
            {envNames.length > 0 ? envNames.join(', ') : '-'}
          </div>
          <div
            slot="anchor"
            style={{
              color: 'var(--exo-color-font)',
              fontWeight: 400,
              fontSize: '14px',
              whiteSpace: 'nowrap',
              alignItems: 'center',
              display: 'inline-flex',
              maxWidth: '100%',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              height: '100%',
              cursor: 'default',
            }}
          >
            {displayText || '-'}
          </div>
        </ExTooltip>
      </div>,
    );

    return container;
  }, []);
  const formatProvider = useCallback((provider: string) => {
    switch (provider) {
      case 'anthropic': return t('models_provider_anthropic');
      case 'gemini': return t('models_provider_gemini');
      case 'openai': return t('models_provider_openai');
      case 'google': return t('models_provider_google');
      default: return provider;
    }
  }, [t]);

  const tooltipCellRenderer = useCallback((value: string) => {
    const container = document.createElement('div');
    const root = createRoot(container);
    const displayValue = value ?? '';

    root.render(
      <ExTooltip
        tooltipDelay={0}
        position={TooltipPosition.TOP}
        alignment={TooltipAlignment.MIDDLE}
      >
        <div style={{
          overflowWrap: 'anywhere',
          width: '100%',
          overflow: 'hidden',
          whiteSpace: 'normal',
        }}
        >
          {displayValue}
        </div>
        <div
          slot="anchor"
          style={{
            display: 'inline-flex',
            maxWidth: '100%',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            cursor: 'default',
          }}
        >
          {displayValue}
        </div>
      </ExTooltip>,
    );

    return container;
  }, []);

  const providerCellRenderer = useCallback((params: any) => (
    tooltipCellRenderer(formatProvider(params.value))
  ), [formatProvider, tooltipCellRenderer]);

  const dateCellRenderer = useCallback((params: any) => (
    tooltipCellRenderer(formatDate(params.value))
  ), [tooltipCellRenderer]);

  const columnDefs = [
    {
      headerName: t('models_table_column_name'),
      field: 'name',
      cellRenderer: nameCellRenderer,
      flex: 1,
      minWidth: 240,
      sortable: true,
      unSortIcon: true,
    },
    {
      headerName: t('models_table_column_environment'),
      field: 'environment',
      cellRenderer: environmentCellRenderer,
      flex: 1,
      minWidth: 240,
      sortable: false,
    },
    {
      headerName: t('models_table_column_provider'),
      field: 'provider',
      cellRenderer: providerCellRenderer,
      flex: 1,
      minWidth: 100,
      sortable: true,
      unSortIcon: true,
    },
    {
      headerName: t('models_table_column_model'),
      field: 'model_count',
      cellRenderer: modelCountCellRenderer,
      flex: 1,
      minWidth: 50,
      sortable: false,
    },
    {
      headerName: t('models_table_column_last_updated'),
      field: 'last_updated_on',
      cellRenderer: dateCellRenderer,
      flex: 1,
      minWidth: 150,
      sortable: true,
      unSortIcon: true,
    },
    {
      headerName: t('models_table_column_created_by'),
      field: 'created_by',
      cellRenderer: (params: any) => tooltipCellRenderer(params.value),
      flex: 1,
      minWidth: 180,
      sortable: false,
    },
    {
      headerName: t('models_table_column_actions'),
      field: 'id',
      cellRenderer: actionCellRenderer,
      flex: 0.8,
      minWidth: 100,
      sortable: false,
      resizable: false,
    },
  ];

  const handleGridReady = useCallback((event: any) => {
    event.api.sizeColumnsToFit();
    window.addEventListener('resize', () => {
      event.api.sizeColumnsToFit();
    });
    gridApiRef.current = event.api;
    setGridApi(event.api);
  }, []);

  const handleSortChanged = useCallback((event: any) => {
    if (event.api) {
      const value = event.api.getColumnState().find((s: any) => s.sort != null);
      if (value) {
        sortModelRef.current = [value];
        setSortModel([value]);
      } else {
        sortModelRef.current = [];
        setSortModel([]);
      }
    }
  }, []);

  useEffect(() => {
    if (sortModel && sortModel.length > 0) {
      onSortChange?.(sortModel[0].colId, sortModel[0].sort);
    } else {
      onSortChange?.();
    }
  }, [sortModel]);

  useEffect(() => {
    if (isLoading) {
      gridApi?.showLoadingOverlay();
    }
    if (gridApi && !isLoading) {
      gridApi.setGridOption('rowData', models);
      setTimeout(() => {
        gridApi.sizeColumnsToFit();
      }, 50);
    }
  }, [gridApi, isLoading, models]);

  const [gridOptions] = useState<any>({
    columnDefs,
    defaultColDef: {
      resizable: true,
      sortable: false,
      cellStyle: { fontSize: '14px', fontFamily: 'Noto Sans' },
    },
    suppressPaginationPanel: true,
    rowHeight: 48,
    suppressCellSelection: true,
    headerHeight: 36,
    animateRows: true,
    enableCellTextSelection: true,
    rowData: [],
    rowSelection: 'single',
    domLayout: 'normal',
    popupParent: document.querySelector('body'),
    suppressDragLeaveHidesColumns: true,
    suppressMovableColumns: true,
    suppressRowTransform: true,
    onGridReady: handleGridReady,
    onSortChanged: handleSortChanged,
    onBodyScroll: (event: any) => {
      if (event?.direction === 'vertical') {
        closeOpenDropdown();
      }
    },
  });

  if (isInitialLoading) {
    return (
      <div className="models-table models-table--loading">
        <ExLoader
          size={LoaderSize.MEDIUM}
          variant={LoaderVariant.SPINNER}
          label={t('models_loading')}
        />
      </div>
    );
  }

  if (showEmptyState && models.length === 0) {
    return (
      <div className="models-table models-table--empty">
        <ExEmptyState
          label={searchTerm !== '' || isAnyFilterActive ? t('model_configs_search_result_empty_state_title') : t('model_configs_empty_state_title')}
          text={searchTerm !== '' || isAnyFilterActive ? t('model_configs_search_result_empty_state_text') : t('model_configs_empty_state_text')}
          data-testid="models-empty-state"
        >
          <div slot="header" data-testid="models-empty-state-header">
            <img alt={t('models_card_no_models_alt')} src={noResultsImage} data-testid="models-empty-state-image" />
          </div>
        </ExEmptyState>
      </div>
    );
  }

  return (
    <div>
      <div className="models-table">
        <ExTable
          gridOptions={gridOptions}
          stickyHeader
          domLayoutNormal
          className={
            hasEditPermission && hasCreatePermission
              ? 'models-table__content'
              : 'models-table__content models-table__content--view-only'
          }
          ref={gridApiRef}
        />
      </div>
      <div>
        {models.length > 0 && (
          <div className="models-table__pagination">
            <ExPagination
              totalItems={totalItems || models.length}
              pageSize={pageSize}
              pageSizeOptions={ITEMS_PER_PAGE_OPTIONS}
              selectedPage={currentPage}
              onChange={(e: CustomEvent) => {
                if (e.detail.pageSize !== pageSize) {
                  onPageSizeChange?.(e.detail.pageSize);
                } else {
                  onPageChange?.(e.detail.selectedPage || 1);
                }
              }}
              className="models-pagination"
            />
          </div>
        )}
      </div>
    </div>
  );
}

export default ModelsTable;
